import { useState } from "react";
import { api } from "@/lib/diningApi";
import { useWaves } from "@/context/DiningWaveContext";
import { toast } from "sonner";
import { Upload, AlertTriangle, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FileInput } from "@/components/FileInput";

export default function ImportExcel() {
  const { currentWave } = useWaves();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [wipe, setWipe] = useState(true);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkMode, setLinkMode] = useState("merge");
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);

  const importLink = async () => {
    const url = linkUrl.trim();
    if (!url) return toast.error("Pega el enlace del export de catering");
    if (linkMode === "replace" && !window.confirm("Modo REEMPLAZAR: se borrarán y sustituirán TODOS los datos de catering (todas las waves) por los del enlace. ¿Continuar?")) return;
    setLinkLoading(true);
    try {
      const { data } = await api.post(`/import/link`, { url, mode: linkMode });
      setLinkResult(data);
      toast.success(`Importado desde catering · ${data.total} registros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar desde el enlace");
    } finally { setLinkLoading(false); }
  };

  const importZip = async () => {
    if (!zipFile) return toast.error("Selecciona el archivo .zip del export");
    if (linkMode === "replace" && !window.confirm("Modo REEMPLAZAR: se borrarán y sustituirán TODOS los datos de catering (todas las waves) por los del archivo. ¿Continuar?")) return;
    setZipLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      fd.append("mode", linkMode);
      const { data } = await api.post(`/import/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLinkResult(data);
      toast.success(`Importado desde catering · ${data.total} registros`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar el archivo");
    } finally { setZipLoading(false); }
  };

  const submit = async () => {
    if (!file) return toast.error("Selecciona un archivo");
    if (wipe && !window.confirm(`Esto borrará TODOS los restaurantes, eventos y facturas de la wave "${currentWave?.name}". ¿Continuar?`)) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/excel`, fd, {
        params: { wipe },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Importado: ${data.created_restaurants} restaurantes, ${data.created_events} eventos`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la importación");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="import-page">
      <div className="mb-8">
        <p className="label-eyebrow">Bootstrap</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Subir Excel</h1>
        <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
          Sube tu plantilla con la planificación general. Formato esperado: filas con ciudad y restaurantes (con precio en columna B), columnas como fechas (mes en fila 0, día de la semana en fila 1, día del mes en fila 2). Celdas con <code className="font-mono">Cda. x2</code> / <code className="font-mono">Cen. x1</code>.
        </p>
      </div>

      <div className="bg-zinc-900 text-white p-5 mb-6 flex items-center gap-3" data-testid="target-wave">
        <Layers className="h-5 w-5" strokeWidth={1.5} />
        <div>
          <p className="text-xs uppercase tracking-wider text-zinc-400">Destino de la importación</p>
          <p className="font-medium mt-0.5">{currentWave?.name || "—"} <span className="text-zinc-400 font-mono text-xs">· {currentWave?.code}</span></p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-900 flex items-start gap-3" data-testid="import-warning">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div>
          <p className="font-medium">Importante</p>
          <p className="mt-1">Después de importar, edita cada restaurante para añadir el <strong>aforo por turno</strong>. Sin aforo no se calculan facturas. StreetXO (con "XO" en el nombre) se configurará con IVA 21% automáticamente.</p>
          <p className="mt-1">¿Empiezas un periodo nuevo? Ve primero a <strong>Waves</strong> y crea uno (puedes clonar restaurantes).</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-8 border-dashed">
        <div className="flex flex-col items-center text-center">
          <Upload className="h-8 w-8 text-zinc-400 mb-3" strokeWidth={1.5} />
          <FileInput file={file} onFile={setFile} accept=".xlsx,.xls" testid="import-file" className="justify-center max-w-full" />
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} data-testid="import-wipe" />
            Borrar datos existentes de esta wave antes de importar
          </label>
          <Button onClick={submit} disabled={loading || !file} className="mt-6 rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="import-submit">
            {loading ? "Importando…" : "Importar a esta wave"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-6 bg-white border border-zinc-200 p-6" data-testid="import-result">
          <p className="label-eyebrow">Resultado</p>
          <p className="mt-2 text-sm">{result.created_restaurants} restaurantes creados</p>
          <p className="text-sm">{result.created_events} eventos creados</p>
          <p className="text-xs text-zinc-500 mt-3">Ahora ve a Restaurantes y configura el aforo por turno de cada uno.</p>
        </div>
      )}

      <div className="mt-12 border-t border-zinc-200 pt-10" data-testid="import-link-section">
        <p className="label-eyebrow">Sincronización</p>
        <h2 className="font-heading text-3xl tracking-tight mt-2">Importar / Actualizar desde catering</h2>
        <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
          Sube el <strong>archivo .zip</strong> del export de la herramienta de catering (con waves, restaurantes, eventos, facturas, empleados y presupuesto), o pega su <strong>enlace</strong>. Se sincronizan todas las waves por su identificador.
        </p>

        <div className="bg-white border border-zinc-200 p-6 mt-6 space-y-5">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs uppercase tracking-wider text-zinc-500">Modo:</span>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="linkmode" checked={linkMode === "merge"} onChange={() => setLinkMode("merge")} data-testid="import-link-merge" />
              Actualizar (mantiene lo que no venga en el archivo)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="linkmode" checked={linkMode === "replace"} onChange={() => setLinkMode("replace")} data-testid="import-link-replace" />
              Reemplazar todo
            </label>
          </div>

          <div className="border border-zinc-200 p-4">
            <label className="text-xs uppercase tracking-wider text-zinc-500">Subir archivo del export (.zip)</label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <FileInput file={zipFile} onFile={setZipFile} accept=".zip,application/zip" testid="import-zip-file" />
              <Button onClick={importZip} disabled={zipLoading || !zipFile} className="rounded-none bg-zinc-900 hover:bg-zinc-800 shrink-0" data-testid="import-zip-submit">
                {zipLoading ? "Importando…" : "Importar archivo"}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-400">
            <span className="h-px flex-1 bg-zinc-200" /> o <span className="h-px flex-1 bg-zinc-200" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Enlace del export (.zip)</label>
            <div className="mt-1.5 flex flex-wrap items-center gap-3">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://…/export_dinning.zip"
                data-testid="import-link-url"
                className="flex-1 min-w-[220px] border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:border-zinc-900"
              />
              <Button onClick={importLink} disabled={linkLoading || !linkUrl.trim()} className="rounded-none bg-zinc-900 hover:bg-zinc-800 shrink-0" data-testid="import-link-submit">
                {linkLoading ? "Importando…" : "Importar desde enlace"}
              </Button>
            </div>
          </div>
        </div>

        {linkResult && (
          <div className="mt-6 bg-white border border-zinc-200 p-6" data-testid="import-link-result">
            <p className="label-eyebrow">Resultado de la sincronización</p>
            <p className="mt-2 text-sm font-medium">{linkResult.total} registros importados ({linkResult.mode === "replace" ? "reemplazo" : "actualización"})</p>
            <ul className="mt-2 text-sm text-zinc-600 space-y-0.5">
              {Object.entries(linkResult.imported || {}).map(([coll, n]) => (
                <li key={coll}><span className="font-mono text-xs">{coll.replace("dining_", "")}</span>: {n}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
